import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SeoHeadProps {
  /**
   * Primary meta title used for OpenGraph/Twitter/SEO tags.
   * The browser tab title will be normalized automatically.
   */
  title?: string;
  /**
   * Optional explicit tab title. When provided it will still be normalized to include the site suffix.
   */
  tabTitle?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  twitterCard?: string;
  twitterCreator?: string;
  twitterSite?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
  schemaType?: 'Organization' | 'Article' | 'Product' | 'WebPage' | 'FAQPage' | 'Event';
  structuredData?: object;
}

const SeoHead: React.FC<SeoHeadProps> = (props) => {
  const {
    title = 'prest0 — AI Employees for Law & Finance',
    tabTitle,
    description = 'Prest0 builds 24/7 AI employees for law and finance teams — research, drafting, ops, and client work on secure, dedicated infrastructure.',
    keywords = 'prest0, AI employees, law, finance, AI research, AI drafting, client intake AI, dedicated AI infrastructure',
    ogImage = '/images/prest0-logo.webp',
    ogUrl,
    ogType = 'website',
    twitterCard = 'summary_large_image',
    twitterCreator = '@prest0ai',
    twitterSite = '@prest0ai',
    twitterTitle,
    twitterDescription,
    twitterImage,
    canonicalUrl,
    noIndex = false,
    schemaType = 'Organization',
    structuredData,
  } = props;
  
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const currentPath = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';
  const fullOgUrl = ogUrl ? ogUrl : siteUrl + currentPath;
  const fullCanonicalUrl = canonicalUrl ? canonicalUrl : fullOgUrl;
  const ogImageUrl = ogImage && ogImage.startsWith('http') ? ogImage : (siteUrl ? siteUrl + ogImage : ogImage);
  
  // Default structured data for Organization
  const defaultOrganizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "prest0",
    "url": "https://prest0.ai",
    "logo": "https://prest0.ai/images/prest0-logo.webp",
    "description": description,
    "sameAs": [
      "https://twitter.com/prest0ai"
    ]
  };

  // Default structured data for WebPage
  const defaultWebPageData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": title,
    "description": description,
    "url": fullCanonicalUrl,
    "publisher": {
      "@type": "Organization",
      "name": "prest0",
      "logo": {
        "@type": "ImageObject",
        "url": "https://prest0.ai/images/prest0-logo.webp"
      }
    }
  };

  // Determine which structured data to use
  const finalStructuredData = structuredData ? structuredData : 
    schemaType === 'Organization' ? defaultOrganizationData : defaultWebPageData;

  const siteName = 'prest0';
  const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const normalizeTabTitle = (raw?: string) => {
    const fallback = siteName;
    if (!raw) return fallback;

    const trimmed = raw.trim();
    if (!trimmed) return fallback;

    const escapedSiteName = escapeRegExp(siteName);
    const stripSiteSuffix = trimmed.replace(new RegExp(`\\s*\\|\\s*${escapedSiteName}$`, 'i'), '').trim();
    const stripLeadingSite = stripSiteSuffix.replace(new RegExp(`^${escapedSiteName}\\s*[:\\-|–]?\\s*`, 'i'), '').trim();

    if (!stripLeadingSite) {
      return siteName;
    }

    if (stripLeadingSite.toLowerCase() === siteName.toLowerCase()) {
      return siteName;
    }

    return `${stripLeadingSite} | ${siteName}`;
  };

  const metaTitle = title || siteName;
  const documentTitle = normalizeTabTitle(tabTitle || title);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{documentTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullOgUrl} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content={twitterSite} />
      <meta name="twitter:creator" content={twitterCreator} />
      <meta name="twitter:title" content={twitterTitle || metaTitle} />
      <meta name="twitter:description" content={twitterDescription || description} />
      <meta name="twitter:image" content={twitterImage || ogImageUrl} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />
      
      {/* No Index if specified */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </Helmet>
  );
};

export default SeoHead; 
